import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Service,
  ServiceQueryParams,
  RequestService,
} from "../types/services.types";

// React Query Keys
export const serviceKeys = {
  all: ['services'] as const,
  list: (page: number, per_page: number, params: Omit<ServiceQueryParams, 'page' | 'per_page'>) => 
    ['services', 'list', page, per_page, params] as const,
  detail: (id: number) => ['services', 'detail', id] as const,
  service: (id: number) => [...serviceKeys.all, id] as const,
};

// Fetch all services with pagination
export const useGetAllServices = (params: ServiceQueryParams = {}) => {
  const { page = 1, per_page = 10, ...queryParams } = params;
  
  const { data, error, ...res } = useQuery({
    queryKey: serviceKeys.list(page, per_page, queryParams),
    queryFn: async (): Promise<Service[]> => {
      const response = await axios.get('/service/list', { 
        params: { page, per_page, ...queryParams }
      });
      return response.data;
    },
  });

  return { services: data ?? [], error, ...res };
};

// Get one service by ID
export const useGetService = (id: number) => {
  return useQuery<Service>({
    queryKey: serviceKeys.service(id),
    queryFn: async () => {
      const response = await axios.get(`/service/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

// Create a service
export const useCreateService = (
  onClose?: () => void,
  initService?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (newService: RequestService) => {
      const response = await axios.post("/service/add", newService);
      return response.data;
    },
    onSuccess: () => {
      onClose?.();
      initService?.();
      queryClient.invalidateQueries({ queryKey: serviceKeys.all });
      toast.success(t("service.createSuccess", "Service created successfully"));
    },
    onError: (err: unknown) => {
      const error = err as { response?: { data?: { code?: string | number; message?: string } } };
      if (error?.response?.data?.code === 401) {
        toast.error(t("service.unauthorized", "You are not authorized to create services"));
      } else if (error?.response?.data?.code === "23000") {
        toast.error(t("service.alreadyExists", "Service with this code already exists"));
      } else if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t("service.createError", "Error creating service"));
      }
    },
  });
};

// Update a service
export const useUpdateService = (
  onClose?: () => void,
  initService?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ serviceId, data }: { serviceId: number; data: RequestService }) => {
      const response = await axios.put(`/service/${serviceId}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      onClose?.();
      initService?.();
      queryClient.invalidateQueries({ queryKey: serviceKeys.detail(variables.serviceId) });
      queryClient.invalidateQueries({ queryKey: serviceKeys.all });
      toast.success(t("service.updateSuccess", "Service updated successfully"));
    },
    onError: (err: unknown) => {
      const error = err as { response?: { data?: { code?: string | number; message?: string } } };
      if (error?.response?.data?.code === 401) {
        toast.error(t("service.unauthorizedUpdate", "You are not authorized to update this service"));
      } else if (error?.response?.data?.code === "23000") {
        toast.error(t("service.codeExists", "A service with this code already exists"));
      } else if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t("service.updateError", "Error updating service"));
      }
    },
  });
};

// Delete a service
export const useDeleteService = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (serviceId: number) => {
      const response = await axios.delete(`/service/${serviceId}`);
      return response.data;
    },
    onSuccess: () => {
      onSuccess?.();
      queryClient.invalidateQueries({ queryKey: serviceKeys.all });
      toast.success(t("service.deleteSuccess", "Service deleted successfully"));
    },
    onError: (err: unknown) => {
      const error = err as { response?: { data?: { code?: string | number; message?: string } } };
      if (error?.response?.data?.code === 401) {
        toast.error(t("service.unauthorizedDelete", "You are not authorized to delete this service"));
      } else if (error?.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t("service.deleteError", "Error deleting service"));
      }
    },
  });
};

// Attach sign categories to a service
export const useAttachCategories = (serviceId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryIds: number[]) => {
      const response = await axios.post(`/service/${serviceId}/categorie`, {
        category_ids: categoryIds
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.service(serviceId) });
      queryClient.invalidateQueries({ queryKey: serviceKeys.detail(serviceId) });
      toast.success(t("service.categoriesAttached", "Categories attached successfully"));
    },
    onError: (err: unknown) => {
      const error = err as { response?: { status?: number; data?: { success?: boolean; message?: string; errors?: Record<string, string[]> } } };
      if (error?.response?.status === 404) {
        toast.error(t("service.notFound", "Service not found"));
      } else if (error?.response?.data?.errors) {
        Object.values(error.response.data.errors).forEach((errorArray) => {
          errorArray.forEach((e: string) => toast.error(e));
        });
      } else {
        toast.error(error?.response?.data?.message || t("service.attachCategoriesError", "Error attaching categories"));
      }
    },
  });
};

// Detach sign categories from a service
export const useDetachCategories = (serviceId: number) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryIds: number[]) => {
      const response = await axios.delete(`/service/${serviceId}/categorie`, {
        data: { category_ids: categoryIds }
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.service(serviceId) });
      queryClient.invalidateQueries({ queryKey: serviceKeys.detail(serviceId) });
      toast.success(t("service.categoriesDetached", "Categories detached successfully"));
    },
    onError: (err: unknown) => {
      const error = err as { response?: { status?: number; data?: { success?: boolean; message?: string; errors?: Record<string, string[]> } } };
      if (error?.response?.status === 404) {
        toast.error(t("service.notFound", "Service not found"));
      } else if (error?.response?.data?.errors) {
        Object.values(error.response.data.errors).forEach((errorArray) => {
          errorArray.forEach((e: string) => toast.error(e));
        });
      } else {
        toast.error(error?.response?.data?.message || t("service.detachCategoriesError", "Error detaching categories"));
      }
    },
  });
};
